import { forwardRef, memo, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { isFunction, isNil } from 'lodash';
// Import Hooks
import { Tooltip, useEllipsisDetection } from '@geneui/components';
// Import Styles
import styles from './typography.module.scss';

interface Props {
    text?: string;
    className?: string;
    maxLines?: number;
    withTooltip?: boolean;
    withEllipsis?: boolean;
}

const Typography = forwardRef<HTMLSpanElement, Props>(
    ({ text, className, maxLines = 1, withTooltip = true, withEllipsis }, ref) => {
        const textRef = useRef<Nullable<HTMLSpanElement>>(null);
        const isEllipsisDetected = useEllipsisDetection(textRef) && withEllipsis;

        const isMultiLine = maxLines > 1;

        // The element is kept in the local ref for the ellipsis detection and shared with the consumer
        const setTextRef = useCallback(
            (element: HTMLSpanElement) => {
                textRef.current = element;

                if (isFunction(ref)) {
                    ref(element);
                } else if (!isNil(ref)) {
                    ref.current = element;
                }
            },
            [ref],
        );

        return (
            <Tooltip title={text} isVisible={withTooltip && isEllipsisDetected}>
                <span
                    ref={setTextRef}
                    style={isMultiLine ? { WebkitLineClamp: maxLines } : undefined}
                    className={classNames(
                        {
                            'ellipsis-text': withEllipsis && !isMultiLine,
                            [styles.ellipsisMultiLine]: withEllipsis && isMultiLine,
                        },
                        className,
                    )}
                >
                    {text}
                </span>
            </Tooltip>
        );
    },
);

Typography.displayName = 'Typography';

export default memo(Typography);
